import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  useDeleteAvatarMutation,
  useGetMediaLimitsQuery,
  useUploadAvatarMutation,
} from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { selectMe, setMeProfile } from "@/state/redux/slices/ui";
import { Button } from "../button";
import { FieldError, FieldLabel } from "../field";
import { formatBytes, fileExtension } from "./MediaInput";
import UserAvatar from "./UserAvatar";

/**
 * Upload or clear your avatar.
 *
 * Validation mirrors the server's image limits, which are the same ones record
 * media uses — an avatar is just an image the account owns. Only the helpers
 * are shared with MediaInput, not acceptMediaFiles: that builds a queue of
 * pending files for a form that saves later, and this uploads one file now.
 */
export default function AvatarInput() {
  const me = useAppSelector(selectMe);
  const dispatch = useAppDispatch();
  const { data: limits } = useGetMediaLimitsQuery();
  const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation();
  const [deleteAvatar, { isLoading: isDeleting }] = useDeleteAvatarMutation();
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const imageLimits = limits?.image;
  const busy = isUploading || isDeleting;
  // display_name is the override; `name` is what is actually shown, and so is
  // what the initials should come from.
  const displayName = me?.name || me?.nickname?.nickname || me?.id;
  // Only an uploaded avatar can be removed. A Google picture is not ours to
  // clear — it comes back on the next sign-in.
  const hasUploadedAvatar = !!me?.picture?.startsWith("/api/user/");

  const handleFile = async (file: File) => {
    setError(null);

    if (imageLimits) {
      const ext = fileExtension(file.name);
      if (!imageLimits.allowed_extensions.includes(ext)) {
        setError(`.${ext || "?"} files are not allowed`);
        return;
      }
      if (file.size > imageLimits.max_size_bytes) {
        setError(`Image exceeds ${formatBytes(imageLimits.max_size_bytes)}`);
        return;
      }
    }

    try {
      const profile = await uploadAvatar(file).unwrap();
      dispatch(setMeProfile(profile));
    } catch (e) {
      setError(
        getApiErrorDetail(e, "Could not upload that image. Please try again."),
      );
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      const profile = await deleteAvatar().unwrap();
      dispatch(setMeProfile(profile));
    } catch (e) {
      setError(
        getApiErrorDetail(e, "Could not remove your avatar. Please try again."),
      );
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">
        Avatar
      </FieldLabel>

      <div className="flex items-center gap-3">
        <UserAvatar
          name={displayName}
          picture={me?.picture}
          className="size-14"
          fallbackClassName="text-base font-medium"
        />

        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {isUploading && <Loader2 className="size-3.5 animate-spin" />}
              {hasUploadedAvatar ? "Replace" : "Upload"}
            </Button>
            {hasUploadedAvatar && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </div>
          {!error && (
            <p className="text-[11px] text-muted-foreground">
              {imageLimits
                ? `Up to ${formatBytes(imageLimits.max_size_bytes)}. Square images work best.`
                : "Square images work best."}
            </p>
          )}
        </div>
      </div>

      {error && <FieldError errors={[{ message: error }]} />}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={
          imageLimits
            ? imageLimits.allowed_extensions.map((e) => `.${e}`).join(",")
            : "image/*"
        }
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset first: picking the same file twice in a row fires no change
          // event otherwise, so a failed upload could not be retried.
          e.target.value = "";
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}
